import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api } from '@/api/client'
import { chatSessionId, discardLegacySharedIdentity, rotateChatSession, visitorId } from '@/auth/identity'
import type { Course, Message } from '@/types'

const THEME_PREFERENCE_VERSION = 'light-default-v1'

export const useAppStore = defineStore('app', () => {
  const courses = ref<Course[]>([])
  const activeCourseId = ref(localStorage.getItem('activeCourseId') || 'signal-systems')
  const studentId = ref(visitorId())
  const sessionId = ref(chatSessionId())
  const messages = ref<Message[]>([])
  const savedTheme = localStorage.getItem('theme')
  const hasCurrentThemePreference = localStorage.getItem('themePreferenceVersion') === THEME_PREFERENCE_VERSION
  const theme = ref<'light'|'dark'>(hasCurrentThemePreference && savedTheme === 'dark' ? 'dark' : 'light')
  const savedProvider = localStorage.getItem('textProvider')
  const textProvider = ref<'deepseek'|'doubao'>(savedProvider === 'doubao' ? 'doubao' : 'deepseek')
  const initialized = ref(false)
  const activeCourse = computed(() => courses.value.find(item => item.id === activeCourseId.value))

  async function initialize() {
    discardLegacySharedIdentity()
    applyTheme(theme.value)
    try {
      courses.value = await api<Course[]>('/courses')
      if (!courses.value.some(item => item.id === activeCourseId.value) && courses.value[0]) activeCourseId.value = courses.value[0].id
      await loadMessages()
    } finally {
      initialized.value = true
    }
  }
  async function refreshCourses() {
    courses.value = await api<Course[]>('/courses')
    if (!courses.value.some(item => item.id === activeCourseId.value) && courses.value[0]) setCourse(courses.value[0].id)
  }
  async function loadMessages() {
    messages.value = (await api<Message[] | null>(`/sessions/${sessionId.value}/messages`).catch(() => [])) ?? []
  }
  function newSession() {
    sessionId.value = rotateChatSession(); messages.value = []
  }
  function setCourse(id:string) { activeCourseId.value=id; localStorage.setItem('activeCourseId',id); newSession() }
  function applyTheme(value:'light'|'dark') { theme.value=value; document.documentElement.dataset.theme=value; localStorage.setItem('theme',value); localStorage.setItem('themePreferenceVersion',THEME_PREFERENCE_VERSION) }
  function setTextProvider(value:'deepseek'|'doubao') {
    if (textProvider.value === value) return
    textProvider.value=value; localStorage.setItem('textProvider',value); newSession()
  }
  return { courses, activeCourseId, activeCourse, studentId, sessionId, messages, theme, textProvider, initialized, initialize, refreshCourses, loadMessages, newSession, setCourse, applyTheme, setTextProvider }
})
